/**
 * Reusable Quiz Card Component
 * Used on My Quizzes page, GeoJSON Preview page, etc.
 */

import { useNavigate } from "react-router-dom";
import { Play, Share2, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getQuizFlag } from "@/utils/quizMetadata";
import { SizeBadge } from "@/components/shared/SizeBadge";
import type { QuizConfig, Category } from "@/db/types";

interface QuizCardProps {
  quiz: QuizConfig;
  onDelete?: (quiz: QuizConfig) => void;
  onShare?: (quiz: QuizConfig) => void;
  onEdit?: (quiz: QuizConfig) => void;
  showActions?: boolean;
}

const getCategoryInfo = (category: Category) => {
  switch (category) {
    case "phone":
      return { emoji: "📞", label: "Phone Area Codes", color: "bg-blue-500/10 text-blue-700" };
    case "cities":
      return { emoji: "🏙️", label: "Cities", color: "bg-purple-500/10 text-purple-700" };
    case "admin":
      return { emoji: "🗺️", label: "Administrative", color: "bg-green-500/10 text-green-700" };
    case "custom":
      return { emoji: "📁", label: "Custom", color: "bg-gray-500/10 text-gray-700" };
    default:
      return { emoji: "📁", label: "Custom", color: "bg-gray-500/10 text-gray-700" };
  }
};

export function QuizCard({
  quiz,
  onDelete,
  onShare,
  onEdit,
  showActions = true,
}: QuizCardProps) {
  const navigate = useNavigate();
  const categoryInfo = getCategoryInfo(quiz.category);

  const lastUsed = new Date(quiz.lastUsedAt);
  const daysAgo = Math.floor(
    (Date.now() - lastUsed.getTime()) / (1000 * 60 * 60 * 24)
  );
  const lastUsedText =
    daysAgo === 0
      ? "Today"
      : daysAgo === 1
        ? "Yesterday"
        : `${daysAgo} days ago`;

  return (
    <Card
      className="border border-border/70 bg-card/80 shadow-lg shadow-primary/10 backdrop-blur hover:shadow-xl hover:shadow-primary/15 transition-all group cursor-pointer flex flex-col"
      onClick={() => navigate(`/quiz/play?signature=${quiz.signature}`)}
    >
      <CardContent className="p-6 flex flex-col flex-1">
        {/* Header with Flag, Title, Size and Category Badges */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 text-3xl leading-none">
            {getQuizFlag(quiz)}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-1 line-clamp-2">
              {quiz.title}
            </h3>
            <p className="text-xs text-muted-foreground">
              {quiz.geography}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <SizeBadge count={quiz.eligibleCount} />
            <Badge
              variant="secondary"
              className={cn("shrink-0 text-xs", categoryInfo.color)}
            >
              {categoryInfo.emoji}
            </Badge>
          </div>
        </div>

        {/* Description */}
        {quiz.description && (
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
            {quiz.description}
          </p>
        )}

        {/* Stats */}
        <div className="space-y-2 mb-4">
          {/* Question count */}
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-foreground/70">Questions:</span>
            <span className="text-muted-foreground font-medium">
              {quiz.eligibleCount}
            </span>
          </div>

          {/* Last played */}
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-foreground/70">Last played:</span>
            <span className="text-muted-foreground">
              {lastUsedText}
            </span>
          </div>
        </div>

        {/* Actions - Pushed to bottom */}
        <div className="flex gap-2 mt-auto pt-4 border-t border-border/30">
          <Button
            size="sm"
            className="flex-1 gap-2 rounded-xl"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/quiz/play?signature=${quiz.signature}`);
            }}
          >
            <Play className="w-4 h-4" />
            Play
          </Button>
          {showActions && (onShare || onEdit || onDelete) && (
            <>
              {onShare && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(quiz);
                  }}
                  title="Share quiz"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              )}
              {onEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(quiz);
                  }}
                  title="Edit quiz"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(quiz);
                  }}
                  title="Delete quiz"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
